import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RenewalGroupHealthPlanInformationFormDTO } from 'core'

import { Button, Modal } from 'common/components'
import { useCommandRequestV2 } from 'common/hooks'

import EditGroupHealthPlanInformation from './EditGroupHealthPlanInformation'
import ViewGroupHealthInformation from './ViewGroupHealthPlanInformation'

const getDefaultGHPFormValues = (
  groupHealthPlanDetails: RenewalGroupHealthPlanInformationFormDTO,
): RenewalGroupHealthPlanInformationFormDTO => {
  return {
    id: groupHealthPlanDetails.id,
    insuranceCarrier: groupHealthPlanDetails.insuranceCarrier,
    planName: groupHealthPlanDetails.planName,
    waitingPeriod: groupHealthPlanDetails.waitingPeriod,
    startDate: groupHealthPlanDetails.startDate,
    endDate: groupHealthPlanDetails.endDate,
    waitingPeriodInput: groupHealthPlanDetails.waitingPeriod,
  }
}

type ConfirmGroupHealthPlanInformationModalProps = {
  updateRenewalGroupHealthPlanDetailsSubmit: (value: RenewalGroupHealthPlanInformationFormDTO) => Promise<any>
  onRenewalGroupPlanInformationUpdated?: () => void
  groupHealthPlanDetails: RenewalGroupHealthPlanInformationFormDTO
  onCancel?: () => void
}

const ConfirmGroupHealthPlanInformationModal: React.FC<ConfirmGroupHealthPlanInformationModalProps> = (props) => {
  const { t: translate } = useTranslation()
  const [viewMode, setViewMode] = useState<boolean>(true)
  const [ghpFormIsValid, setGhpFormIsValid] = useState<boolean>(false)
  const [isSubmitHandlerLoading, setIsSubmitHandlerLoading] = useState(false)

  const [ghpValues, setGhpValues] = useState<RenewalGroupHealthPlanInformationFormDTO>(
    getDefaultGHPFormValues(props.groupHealthPlanDetails),
  )
  const { sendCommand: updateGroupHealthPlan } = useCommandRequestV2((values: any) =>
    props.updateRenewalGroupHealthPlanDetailsSubmit(values),
  )

  const useSameGroupHealthPlanInformation = async () => {
    setIsSubmitHandlerLoading(true)
    const result = await updateGroupHealthPlan({ useSame: true })
    if (result && props.onRenewalGroupPlanInformationUpdated) {
      props.onRenewalGroupPlanInformationUpdated()
    }
    setIsSubmitHandlerLoading(false)
  }

  const footer = (
    <div>
      <Button type="default" onClick={() => setViewMode(false)}>
        {translate('admin-dashboard.home.confirm-payment-method.make-changes')}
      </Button>
      <Button
        type="primary"
        onClick={useSameGroupHealthPlanInformation}
        disabled={isSubmitHandlerLoading}
        loading={isSubmitHandlerLoading}
      >
        {translate('admin-dashboard.home.confirm-payment-method.use-same')}
      </Button>
    </div>
  )

  const editFooter = (
    <div>
      <Button type="default" onClick={() => setViewMode(true)}>
        {translate('admin-dashboard.home.confirm-payment-method.go-back')}
      </Button>
      <Button
        form="groupHealthPlanInformationForm"
        type="primary"
        htmlType="submit"
        disabled={isSubmitHandlerLoading}
        loading={isSubmitHandlerLoading}
        onClick={() => submit(ghpValues)}
      >
        {translate('general.save')}
      </Button>
    </div>
  )

  const submit = async (values?: RenewalGroupHealthPlanInformationFormDTO) => {
    try {
      setIsSubmitHandlerLoading(true)
      const result = await updateGroupHealthPlan({ useSame: false, ...values })
      if (result && props.onRenewalGroupPlanInformationUpdated) {
        props.onRenewalGroupPlanInformationUpdated()
      }
      setIsSubmitHandlerLoading(false)
    } catch (errors) {
      setIsSubmitHandlerLoading(false)
    }
  }

  return (
    <Modal
      width="650px"
      visible={true}
      onCancel={() => props.onCancel()}
      className={'justify_center'}
      title={translate('admin-signup.company-details.health-plan.health-plan-title')}
      footer={viewMode ? footer : editFooter}
    >
      {viewMode ? (
        <ViewGroupHealthInformation groupHealthPlanDetails={ghpValues} />
      ) : (
        <EditGroupHealthPlanInformation
          groupHealthPlanInformation={ghpValues}
          setGhpValues={setGhpValues}
          setGhpFormIsValid={setGhpFormIsValid}
        />
      )}
    </Modal>
  )
}

export default ConfirmGroupHealthPlanInformationModal
